import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const TONE_CLASSES = {
  emerald: { card: 'bg-emerald-50 border-emerald-200', label: 'text-emerald-700', value: 'text-emerald-900' },
  blue:    { card: 'bg-blue-50 border-blue-200',       label: 'text-blue-700',    value: 'text-blue-900' },
  slate:   { card: 'bg-slate-50 border-slate-200',     label: 'text-slate-600',   value: 'text-slate-800' },
  amber:   { card: 'bg-amber-50 border-amber-200',     label: 'text-amber-700',   value: 'text-amber-900' },
};

const CountCard = ({ label, value, tone, testId }) => {
  const t = TONE_CLASSES[tone] || TONE_CLASSES.slate;
  return (
    <div className={`border rounded-md p-3 ${t.card}`}>
      <p className={`text-xs uppercase tracking-wide ${t.label}`}>{label}</p>
      <p className={`text-2xl font-heading font-bold ${t.value}`} data-testid={testId}>
        {value ?? 0}
      </p>
    </div>
  );
};

/**
 * Generic Excel-import result dialog. Used by both Drivers and Vehicles pages.
 * @param {object} props.result - { created, updated, skipped, linked_to_vehicle?, errors[] }
 * @param {string} props.matchedByLabel - e.g. "ID Number" (drivers) or "Registration" (vehicles)
 * @param {string} [props.extraNote] - optional additional explanation shown below match rule
 */
const ImportResultDialog = ({
  open,
  onOpenChange,
  result,
  matchedByLabel = 'ID Number',
  extraNote,
  testIdPrefix = 'import',
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid={`${testIdPrefix}-result-dialog`}>
      <DialogHeader>
        <DialogTitle className="font-heading">Import complete</DialogTitle>
        <DialogDescription>
          Here&apos;s a summary of what was imported from your Excel file.
        </DialogDescription>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-3 mt-2">
        <CountCard label="Created" value={result?.created} tone="emerald" testId={`${testIdPrefix}-created-count`} />
        <CountCard label="Updated" value={result?.updated} tone="blue"    testId={`${testIdPrefix}-updated-count`} />
        <CountCard label="Skipped" value={result?.skipped} tone="slate"   testId={`${testIdPrefix}-skipped-count`} />
        {result?.linked_to_vehicle !== undefined && (
          <CountCard
            label="Linked to vehicle"
            value={result?.linked_to_vehicle}
            tone="amber"
            testId={`${testIdPrefix}-linked-count`}
          />
        )}
      </div>
      {result?.errors?.length > 0 && (
        <div className="mt-3 border rounded-md p-3 bg-red-50 border-red-200">
          <p className="text-xs font-semibold text-red-700 mb-1">Issues ({result.errors.length})</p>
          <ul className="text-xs text-red-800 space-y-0.5 max-h-32 overflow-auto">
            {result.errors.map((err, i) => <li key={i}>• {err}</li>)}
          </ul>
        </div>
      )}
      <p className="text-xs text-slate-500 mt-2">
        Rows are matched by <span className="font-semibold">{matchedByLabel}</span> — existing matches are updated, unmatched rows create new records.
        {extraNote && <> {extraNote}</>}
      </p>
      <div className="flex justify-end mt-3">
        <Button onClick={() => onOpenChange(false)} data-testid={`${testIdPrefix}-close-btn`}>Done</Button>
      </div>
    </DialogContent>
  </Dialog>
);

export default ImportResultDialog;
